import type { AppLocale } from "./types";

/** Kalıcı dil tercihi (landing + davet). */
export const LOCALE_STORAGE_KEY = "wedy.locale";

/** Dil seçicide gösterim sırası (İngilizce → … kullanıcı listesi). */
export const ALL_APP_LOCALES: readonly AppLocale[] = [
  "en",
  "ar",
  "zh-Hans",
  "da",
  "nl",
  "fr",
  "de",
  "he",
  "hi",
  "id",
  "it",
  "ja",
  "no",
  "pt-PT",
  "ru",
  "sk",
  "es",
  "tr",
  "uk",
  "vi",
] as const;

/** Dil seçicide yerel adlar. */
export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: "English",
  ar: "العربية",
  "zh-Hans": "简体中文",
  da: "Dansk",
  nl: "Nederlands",
  fr: "Français",
  de: "Deutsch",
  he: "עברית",
  hi: "हिन्दी",
  id: "Bahasa Indonesia",
  it: "Italiano",
  ja: "日本語",
  no: "Norsk",
  "pt-PT": "Português (Portugal)",
  ru: "Русский",
  sk: "Slovenčina",
  es: "Español",
  tr: "Türkçe",
  uk: "Українська",
  vi: "Tiếng Việt",
};

/** Dil seçicide kısa gösterim: bayrak + kod (ör. 🇹🇷 TR). `title` ile tam ad ayrı verilir. */
export const LOCALE_SELECT_LABELS: Record<AppLocale, string> = {
  en: "🇺🇸 EN",
  ar: "🇸🇦 AR",
  "zh-Hans": "🇨🇳 ZH",
  da: "🇩🇰 DA",
  nl: "🇳🇱 NL",
  fr: "🇫🇷 FR",
  de: "🇩🇪 DE",
  he: "🇮🇱 HE",
  hi: "🇮🇳 HI",
  id: "🇮🇩 ID",
  it: "🇮🇹 IT",
  ja: "🇯🇵 JA",
  no: "🇳🇴 NO",
  "pt-PT": "🇵🇹 PT",
  ru: "🇷🇺 RU",
  sk: "🇸🇰 SK",
  es: "🇪🇸 ES",
  tr: "🇹🇷 TR",
  uk: "🇺🇦 UK",
  vi: "🇻🇳 VI",
};

/** BCP 47 — Intl.DateTimeFormat vb. */
export function getIntlLocale(app: AppLocale): string {
  const map: Record<AppLocale, string> = {
    en: "en-US",
    ar: "ar",
    "zh-Hans": "zh-CN",
    da: "da-DK",
    nl: "nl-NL",
    fr: "fr-FR",
    de: "de-DE",
    he: "he-IL",
    hi: "hi-IN",
    id: "id-ID",
    it: "it-IT",
    ja: "ja-JP",
    no: "nb-NO",
    "pt-PT": "pt-PT",
    ru: "ru-RU",
    sk: "sk-SK",
    es: "es",
    tr: "tr-TR",
    uk: "uk-UA",
    vi: "vi-VN",
  };
  return map[app] ?? "en-US";
}

export function isRtlLocale(app: AppLocale): boolean {
  return app === "ar" || app === "he";
}

function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/_/g, "-");
}

/** `?lang=` / `?locale=` veya localStorage değeri. */
export function parseLocaleParam(raw: string): AppLocale | null {
  const t = normalizeTag(raw);
  const aliases: Record<string, AppLocale> = {
    en: "en",
    "en-us": "en",
    "en-gb": "en",
    tr: "tr",
    ar: "ar",
    fr: "fr",
    "fr-fr": "fr",
    de: "de",
    "de-de": "de",
    it: "it",
    "pt-pt": "pt-PT",
    "pt-PT": "pt-PT",
    pt: "pt-PT",
    "pt-br": "pt-PT",
    ru: "ru",
    ja: "ja",
    hi: "hi",
    id: "id",
    nl: "nl",
    "nl-nl": "nl",
    da: "da",
    "da-dk": "da",
    no: "no",
    nb: "no",
    "nb-no": "no",
    "nn-no": "no",
    he: "he",
    "he-il": "he",
    sk: "sk",
    "sk-sk": "sk",
    uk: "uk",
    "uk-ua": "uk",
    vi: "vi",
    es: "es",
    "es-es": "es",
    "es-419": "es",
    "es-mx": "es",
    "es-ar": "es",
    "zh-hans": "zh-Hans",
    "zh-cn": "zh-Hans",
    "zh-sg": "zh-Hans",
    /** Eski sürüm — artık yok; Basitleştirilmiş Çince’ye yönlendir. */
    "zh-hant": "zh-Hans",
    "zh-tw": "zh-Hans",
    "zh-hk": "zh-Hans",
    "zh-mo": "zh-Hans",
    /** Kaldırılan diller — İngilizceye yönlendir. */
    ko: "en",
    "ko-kr": "en",
    sv: "en",
    "sv-se": "en",
    th: "en",
    "th-th": "en",
  };
  if (aliases[t]) return aliases[t];
  if ((ALL_APP_LOCALES as readonly string[]).includes(t)) return t as AppLocale;
  return null;
}

export function persistAppLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* private mode vb. */
  }
}

function matchLocale(tag: string): AppLocale | null {
  const t = normalizeTag(tag);
  if (t === "zh-tw" || t === "zh-hk" || t === "zh-mo" || t === "zh-hant") return "zh-Hans";
  if (t === "zh-cn" || t === "zh-sg" || t === "zh-hans") return "zh-Hans";
  if (t === "zh") return "zh-Hans";

  if (t.startsWith("pt")) return "pt-PT";

  if (t.startsWith("es")) return "es";

  if (t === "vi" || t.startsWith("vi-")) return "vi";

  if (t === "ko" || t.startsWith("ko-")) return "en";
  if (t === "sv" || t.startsWith("sv-")) return "en";
  if (t === "th" || t.startsWith("th-")) return "en";

  if (t === "he" || t.startsWith("he-")) return "he";

  if (t === "da" || t.startsWith("da-")) return "da";

  if (t === "no" || t.startsWith("no-") || t.startsWith("nb") || t.startsWith("nn")) return "no";

  if (t === "sk" || t.startsWith("sk-")) return "sk";

  if (t === "uk" || t.startsWith("uk-")) return "uk";

  const primary = t.split("-")[0] ?? "";
  if (!primary) return null;
  if (primary === "zh") return "zh-Hans";

  const supported = new Set<string>([
    "en",
    "tr",
    "ar",
    "fr",
    "de",
    "it",
    "ru",
    "ja",
    "hi",
    "id",
    "nl",
  ]);
  if (supported.has(primary)) return primary as AppLocale;
  return null;
}

/**
 * Öncelik: `?lang=` / `?locale=` → localStorage (`wedy.locale`) → navigator → `en`.
 * URL’de geçerli dil varsa localStorage’a yazılır.
 */
export function resolveAppLocale(): AppLocale {
  const params = new URLSearchParams(window.location.search);
  for (const key of ["lang", "locale"]) {
    const raw = params.get(key);
    if (raw) {
      const p = parseLocaleParam(raw);
      if (p) {
        persistAppLocale(p);
        return p;
      }
    }
  }

  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) {
      const p = parseLocaleParam(stored);
      if (p) return p;
    }
  } catch {
    /* ignore */
  }

  const candidates = [navigator.language, ...(navigator.languages ?? [])];
  for (const c of candidates) {
    const m = matchLocale(c);
    if (m) return m;
  }
  return "en";
}
